import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radar, Loader2, CheckSquare, Square } from "lucide-react";
import { api, errorMessage, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useScanJob } from "@/components/AdMentionsField";

export default function TimecodesAdmin() {
  const [subs, setSubs] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [term, setTerm] = useState("Lucky Rebel");
  const { job, setJob, track, running } = useScanJob("/admin/timecodes");

  const load = () => api.get("/admin/timecodes/submissions").then((r) => { setSubs(r.data); setChecked(new Set(r.data.map((s) => s.id))); }).catch(() => setSubs([]));
  useEffect(() => { load(); }, []);

  const toggle = (id) => setChecked((c) => { const n = new Set(c); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const all = subs?.length && checked.size === subs.length;

  const scan = async () => {
    if (!checked.size) return toast.error("Tick at least one episode");
    try {
      const { data } = await api.post("/admin/timecodes/scan", { submission_ids: [...checked], term: term.trim() || "Lucky Rebel" });
      setJob(data);
      toast.info(`Scanning ${checked.size} episode${checked.size === 1 ? "" : "s"} for “${term}” — a few minutes per hour of audio`);
      track(data._id, () => { toast.success("Scan complete"); load(); });
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const result = (id) => job?.items?.find((it) => it.submission_id === id);
  if (!subs) return null;
  return (
    <div data-testid="admin-timecodes">
      <div className="mb-8">
        <p className="eyebrow mb-2">Campaigns</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Timecodes</h1>
        <p className="mt-2 text-sm text-slate-400">Every episode submitted in the past 3 weeks. Tick the ones to include, then scan their audio for a sponsor term. Results are saved on each submission.</p>
      </div>
      <div className="card-surface rounded-2xl p-4 mb-6 flex flex-wrap items-end gap-4" data-testid="timecodes-controls">
        <div className="min-w-[220px] flex-1"><Label>Term to find</Label><Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Lucky Rebel" className="mt-1.5 bg-white/5 border-white/10" data-testid="timecodes-term-input" /></div>
        <button type="button" onClick={() => setChecked(all ? new Set() : new Set(subs.map((s) => s.id)))} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white pb-2.5" data-testid="timecodes-toggle-all">{all ? <CheckSquare size={16} /> : <Square size={16} />} {all ? "Uncheck all" : "Check all"}</button>
        <Button onClick={scan} disabled={running || !subs.length} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="timecodes-scan-button">{running ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Radar size={15} className="mr-2" />} {running ? "Scanning…" : `Scan ${checked.size} episode${checked.size === 1 ? "" : "s"}`}</Button>
      </div>
      <div className="space-y-2" data-testid="timecodes-list">
        {subs.length === 0 && <p className="text-sm text-slate-400" data-testid="timecodes-empty">No submissions in the past 3 weeks.</p>}
        {subs.map((s, i) => {
          const r = result(s.id);
          const saved = s.scan;
          return (
            <label key={s.id} className={`card-surface rounded-2xl p-4 flex items-start gap-4 cursor-pointer ${checked.has(s.id) ? "" : "opacity-60"}`} data-testid={`timecodes-row-${i}`}>
              <input type="checkbox" checked={checked.has(s.id)} onChange={() => toggle(s.id)} className="mt-1 accent-sky-400" data-testid={`timecodes-check-${i}`} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{s.title}</p>
                <p className="text-xs text-slate-400 truncate">{s.show_name} · by {s.host_name} · submitted {timeAgo(s.created_at)}{s.ad_mentions ? ` · host noted: ${s.ad_mentions} (${s.ad_mention_count})` : ""}</p>
                {r?.status === "pending" && <p className="mt-1 text-xs text-slate-500">Waiting…</p>}
                {r?.status === "failed" && <p className="mt-1 text-xs text-rose-400">{r.error}</p>}
                {(r?.status === "done" || (!r && saved)) && (
                  <p className="mt-1 text-xs font-mono text-emerald-300" data-testid={`timecodes-result-${i}`}>
                    “{r ? job.term : saved.term}”: {(r ? r.timecodes : saved.timecodes).join(", ") || "no mentions found"} ({r ? r.count : saved.count}){!r && saved ? <span className="text-slate-500"> · scanned {timeAgo(saved.scanned_at)}</span> : null}
                  </p>
                )}
              </div>
              {running && r?.status === "pending" && <Loader2 size={16} className="animate-spin text-sky-300 mt-1" />}
            </label>
          );
        })}
      </div>
    </div>
  );
}
